import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';

import APP_CONSTANTS from '../../constants';
import * as actions from '../../store/state/actions/action-creators';
import * as StateManager from '../../store/state/state-manager';
import { buildContextName, buildModalContext } from '../../utils/context-utils';
import { processDataToDispatchErrorAction } from '../../store/state/epics/util';
import { isErrorActionType, promiseHandler } from '../../case/common-helper';
import type { Action } from '../../store/state/state-manager';
import type { Actionout } from '../../types';
import * as ContainerUtils from '../../container/container-utils';
import { getLocaleUtils } from '../../locale/locale-utils';
import { publishDisplayCreatedCaseToasterEvent } from '../../case/create-work/events';
import { ActionManagerInstance } from '../../actions/manager';
import { getResolvedTargetName } from '../../router/routing-manager';
import { CONTAINER_NAMES } from '../../container/container-utils';

import { DELETE_WORK } from './action-creator';
import { executeDeleteWorkRequest } from './async-request';
import { publishWorkDeletedEvent } from './events';

const deleteWork = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let caseTypeID: string;
  let actionsOut: Actionout[] = [];

  return action$.pipe(
    ofType(DELETE_WORK),
    map((action: Action) => {
      ({ actionMgrID, caseTypeID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      // Show confirmation modal — dispatch directly and terminate this stream path
      if (!actionInPayload.skipDeleteConfirmation && !actionInPayload.ignoreWorkDeletion) {
        const containerName = CONTAINER_NAMES.MODAL;
        const target = getResolvedTargetName(containerName, actionInPayload.context);
        const context = buildModalContext(
          actionInPayload.context,
          buildContextName(containerName, actionInPayload.context)
        );

        StateManager.dispatch([
          actions.postProcess(actions.addContainerItem, {
            target,
            context,
            containerItemID: context,
            containerName,
            isDeleteConfirmation: true,
            actionName: 'Delete',
            workId: actionInPayload.workId,
            caseTypeID,
            className: actionInPayload.className,
            keys: actionInPayload.keys,
            ...(actionInPayload.enableBackNavigation && { enableBackNavigation: true }),
            data: {
              caseInfo: {
                caseTypeID
              }
            }
          })
        ]);
        ActionManagerInstance.resolveActionByID(actionMgrID);
        return EMPTY;
      }

      // Skip deletion (e.g. preview teardown)
      if (actionInPayload.ignoreWorkDeletion) {
        return of({ actionInPayload });
      }

      // Execute actual deletion
      return executeDeleteWorkRequest(actionInPayload);
    }),
    mergeMap(async (data) => {
      if (data && isErrorActionType(data.type)) {
        const { payload } = data;
        actionsOut.push(processDataToDispatchErrorAction({ ...data, ...payload, actionMgrID }));
        return actionsOut;
      }

      const { actionInPayload } = data;
      const { context } = actionInPayload;

      publishDisplayCreatedCaseToasterEvent({
        content: getLocaleUtils().getLocaleValue('Deleted successfully!', 'Messages')
      });

      // For preview scenario, we need to close the preview panel
      if (context?.includes(APP_CONSTANTS.PREVIEW)) {
        actionsOut.push(
          actions.postProcess(
            actions.resetContainers,
            ContainerUtils.resetContainersPayload({
              context: APP_CONSTANTS.APP.APP,
              name: APP_CONSTANTS.PREVIEW
            })
          )
        );
      }

      // For case page scenario, we need to return to the previous screen
      if (context?.includes(APP_CONSTANTS.MODAL)) {
        const modalContainerName = CONTAINER_NAMES.MODAL;
        const modalTarget = getResolvedTargetName(modalContainerName, context);
        const containerItem = ContainerUtils.getContainerItemData(modalTarget, context);
        if (containerItem?.enableBackNavigation) {
          window?.history?.back();
        }
      }

      return actionsOut;
    }),
    map(() => {
      StateManager.dispatch(actionsOut);
      if (promiseHandler(actionsOut, actionMgrID) === APP_CONSTANTS.PROMISE_VALUE.RESOLVE) {
        publishWorkDeletedEvent({ caseTypeID });
      }
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default deleteWork;
